import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { useMemo, useState, type ReactNode } from 'react'
import { CalendarRange } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryKey } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  Banner,
  Card,
  DateField,
  EmptyState,
  ErrorState,
  MoneyValue,
  SegmentedControl,
  Skeleton,
} from '@shared/design-system/ui'
import { button as buttonRecipe } from '@shared/design-system/ui/Button/Button.css'
import { toast } from '@shared/lib/toast/toast-store'
import { getDefaultCustomDateRange } from '@shared/lib/date-range'
import { analyticsPeriod } from '@shared/lib/analytics-period'
import { useInViewOnce } from '@shared/lib/use-in-view'
import type { Currency } from '@shared/contracts/enums'
import { subscriptionMetricsQuery } from '@entities/subscription-metrics'
import { getSalesPage } from '@entities/sale/sale.queries'
import {
  awardsListQuery,
  awardsPerformanceQuery,
  dashboardKeys,
  dashboardOverviewQuery,
  requestAward,
} from '../api/dashboard.queries'
import type { DashboardOverview, DashboardRange, DashboardView } from '../api/dashboard.contract'
import { AwardsStrip } from './AwardsStrip'
import { oldestFinancialComputation } from '../api/dashboard.freshness'
import { FreshnessBar } from './FreshnessBar'
import { FinancialSummaryPanel } from '@entities/financial-summary'
import { KpiRow } from './KpiRow'
import { ReferralBalanceCard } from './ReferralBalanceCard'
import { GrossSalesChart } from './GrossSalesChart'
import type { ChartMode } from './gross-sales-series'
import { LiveGrossSales } from './LiveGrossSales'
import { emptyCurrencyOverview } from '../api/dashboard.empty'
import { LatestSales } from './LatestSales'
import { OriginAnalytics } from './OriginAnalytics'
import { PaymentMethodBreakdown } from './PaymentMethodBreakdown'
import { RecurrencePanel } from './RecurrencePanel'
import { TopProducts } from './TopProducts'
import {
  analyticsGrid,
  balanceTeaser,
  balanceTeaserMeta,
  balanceTeaserText,
  customRangeRow,
  customRangeNotice,
  dashboardGroup,
  dashboardHeader,
  filtersRow,
  headerCopy,
  kpiGrid,
  page,
  periodFilters,
  sectionSubtitle,
  sectionTitle,
  title,
} from './dashboard.css'

export interface DashboardSearch {
  range?: DashboardRange
  from?: string
  to?: string
  currency?: Currency
  view?: DashboardView
  productId?: string
  live?: boolean
}

interface DashboardScreenProps {
  referralReceivingNotice?: ReactNode
  search: DashboardSearch
  onSearchChange: (next: DashboardSearch) => void
  onViewSale: (saleId: string, scope?: 'charge') => void
}

const DEFAULT_RANGE: DashboardRange = '30d'
const DEFAULT_VIEW: DashboardView = 'all'
const AWARDS_STALE_MS = 5 * 60_000
const MAX_DASHBOARD_CUSTOM_RANGE_DAYS = 366
const DAY_IN_MS = 86_400_000
const CUSTOM_RANGE_LIMIT_MESSAGE =
  'O período máximo permitido no Dashboard é de 366 dias. Reduza o intervalo selecionado.'

const rangeOptions = [
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: 'month', label: 'Este mês' },
  { value: 'custom', label: 'Personalizado' },
] satisfies ReadonlyArray<{ value: DashboardRange; label: string }>

function pruneSearch(search: DashboardSearch): DashboardSearch {
  const next: DashboardSearch = {}
  if (search.range && search.range !== DEFAULT_RANGE) next.range = search.range
  if (search.range === 'custom' && search.from) next.from = search.from
  if (search.range === 'custom' && search.to) next.to = search.to
  if (search.currency) next.currency = search.currency
  if (search.productId) next.productId = search.productId
  if (search.live) next.live = true
  return next
}

function DashboardSkeleton() {
  return (
    <div className={page} data-testid="dashboard-screen">
      <div className={dashboardHeader}>
        <div className={headerCopy}>
          <Skeleton width="22%" height={28} />
          <Skeleton width="42%" height={14} />
        </div>
      </div>
      <div className={filtersRow}>
        <Skeleton width="100%" height={44} />
      </div>
      <div className={kpiGrid}>
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <Card key={index} padding="md">
            <Skeleton width="50%" height={12} />
            <div style={{ height: 14 }} />
            <Skeleton width="70%" height={28} />
          </Card>
        ))}
      </div>
      <Card padding="md">
        <Skeleton width="100%" height={280} />
      </Card>
    </div>
  )
}

interface DashboardFiltersProps {
  customRangeError?: string
  data?: DashboardOverview
  range: DashboardRange
  search: DashboardSearch
  selectedCurrency?: Currency
  updateSearch: (patch: DashboardSearch) => void
}

function DashboardFilters({
  customRangeError,
  range,
  search,
  updateSearch,
}: DashboardFiltersProps) {
  return (
    <div className={filtersRow}>
      <div className={periodFilters}>
        <SegmentedControl
          value={range}
          options={rangeOptions}
          ariaLabel="Período do dashboard"
          onChange={(nextRange) => {
            if (nextRange === 'custom') {
              const customRange =
                search.from && search.to
                  ? { from: search.from, to: search.to }
                  : getDefaultCustomDateRange()
              updateSearch({ range: nextRange, ...customRange })
              return
            }

            updateSearch({ range: nextRange, from: undefined, to: undefined })
          }}
        />
      </div>
      {range === 'custom' ? (
        <div className={customRangeRow}>
          <DateField
            label="De"
            value={search.from ?? ''}
            onChange={(event) => {
              updateSearch({ from: event.target.value || undefined })
            }}
          />
          <DateField
            label="Até"
            value={search.to ?? ''}
            status={customRangeError ? 'error' : undefined}
            aria-describedby={customRangeError ? 'dashboard-custom-range-error' : undefined}
            onChange={(event) => {
              updateSearch({ to: event.target.value || undefined })
            }}
          />
          {customRangeError ? (
            <div id="dashboard-custom-range-error" className={customRangeNotice}>
              <Banner tone="danger" title="Período máximo de 366 dias no dashboard">
                Reduza o intervalo selecionado.
              </Banner>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function DashboardHeader() {
  return (
    <div className={dashboardHeader}>
      <div className={headerCopy}>
        <h1 className={title}>Dashboard de vendas</h1>
        <span className={sectionSubtitle}>
          Vendas criadas, dinheiro recebido e eficiência comercial em reais.
        </span>
      </div>
    </div>
  )
}

export function DashboardScreen({
  search,
  onSearchChange,
  onViewSale,
  referralReceivingNotice,
}: DashboardScreenProps) {
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  const queryClient = useQueryClient()
  const [chartMode, setChartMode] = useState<ChartMode>('cumulative')
  const [, setCalendarTick] = useState(0)
  const range = search.range ?? DEFAULT_RANGE
  const view = DEFAULT_VIEW
  const period = analyticsPeriod({
    preset: range,
    from: range === 'custom' ? (search.from ?? null) : null,
    to: range === 'custom' ? (search.to ?? null) : null,
  })
  const syncCalendar = () => {
    if (range === 'custom') return false
    const now = new Date()
    if (analyticsPeriod({ preset: range, from: null, to: null }, now).query === period.query)
      return false
    setCalendarTick((tick) => tick + 1)
    return true
  }

  const resolvedDates = { dateFrom: period.from, dateTo: period.to }
  const hasValidCustomRange =
    range !== 'custom' ||
    Boolean(
      search.from &&
      search.to &&
      search.from <= search.to &&
      resolvedDates.dateFrom &&
      resolvedDates.dateTo,
    )
  const customRangeDays =
    search.from && search.to
      ? (Date.parse(search.to) - Date.parse(search.from)) / DAY_IN_MS + 1
      : undefined
  const customRangeError =
    range === 'custom' &&
    customRangeDays !== undefined &&
    customRangeDays > MAX_DASHBOARD_CUSTOM_RANGE_DAYS
      ? CUSTOM_RANGE_LIMIT_MESSAGE
      : undefined
  const canQueryDashboard = hasValidCustomRange && !customRangeError
  const liveOpen = Boolean(search.live) && canQueryDashboard
  const overviewFilters = useMemo(
    () => ({
      range,
      from: resolvedDates.dateFrom,
      to: resolvedDates.dateTo,
      productId: search.productId,
      view,
    }),
    [range, resolvedDates.dateFrom, resolvedDates.dateTo, search.productId, view],
  )

  const overviewQuery = dashboardOverviewQuery(overviewFilters)
  const overview = useQuery({
    ...overviewQuery,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    enabled: canQueryDashboard && !liveOpen,
  })
  const secondaryEnabled = canQueryDashboard && !liveOpen && overview.isSuccess
  const [awardsRef, awardsInView] = useInViewOnce<HTMLDivElement>('200px 0px')
  const recurrenceQuery = subscriptionMetricsQuery(
    period.query + (search.productId ? `&productId=${encodeURIComponent(search.productId)}` : ''),
  )
  const recurrence = useQuery({
    ...recurrenceQuery,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: secondaryEnabled,
  })
  const awardsPerformance = useQuery({
    ...awardsPerformanceQuery(),
    staleTime: AWARDS_STALE_MS,
    refetchOnWindowFocus: false,
    enabled: awardsInView && !liveOpen,
  })
  const awardsList = useQuery({
    ...awardsListQuery(),
    staleTime: AWARDS_STALE_MS,
    refetchOnWindowFocus: false,
    enabled: awardsInView && !liveOpen,
  })
  const awardRequest = useMutation({
    mutationFn: requestAward,
    onSuccess: async () => {
      toast.success({
        title: 'Solicitação enviada',
        description: 'Seu pedido de premiação foi registrado.',
      })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: dashboardKeys.awardsPerformance() }),
        queryClient.invalidateQueries({ queryKey: dashboardKeys.awardsList() }),
      ])
    },
    onError: () => {
      toast.error({
        title: 'Não foi possível solicitar o prêmio',
        description: 'Revise os dados e tente novamente.',
      })
    },
  })
  const selectedCurrency = 'BRL'
  const latestSalesKey = [
    'dashboard',
    'latest-sales',
    resolvedDates.dateFrom,
    resolvedDates.dateTo,
    selectedCurrency,
    search.productId,
  ] as const
  const latestSales = useQuery({
    queryKey: latestSalesKey,
    queryFn: () =>
      getSalesPage({
        dashboard: true,
        limit: 20,
        currency: selectedCurrency,
        productId: search.productId,
        dateFrom: resolvedDates.dateFrom,
        dateTo: resolvedDates.dateTo,
      }),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    enabled: secondaryEnabled,
  })

  const refreshInTiers = async () => {
    const refetchTier = (queryKeys: readonly QueryKey[]) =>
      Promise.all(
        queryKeys.map((queryKey) =>
          queryClient.refetchQueries(
            {
              queryKey,
              exact: true,
              type: 'active',
            },
            { cancelRefetch: false },
          ),
        ),
      )
    if (!syncCalendar()) {
      await refetchTier([overviewQuery.queryKey])
      if (queryClient.getQueryState(overviewQuery.queryKey)?.status !== 'success') {
        return
      }
      await refetchTier([latestSalesKey, recurrenceQuery.queryKey])
    }
    await refetchTier([dashboardKeys.awardsPerformance(), dashboardKeys.awardsList()])
  }

  const updateSearch = (patch: DashboardSearch) => {
    onSearchChange(pruneSearch({ ...search, ...patch }))
  }

  if (!canQueryDashboard) {
    return (
      <div className={page} data-testid="dashboard-screen">
        <DashboardHeader />
        <DashboardFilters
          customRangeError={customRangeError}
          range={range}
          search={search}
          updateSearch={updateSearch}
        />
        <Card padding="lg">
          <EmptyState
            icon={<CalendarRange size={22} />}
            title={
              customRangeError ? 'Reduza o período personalizado' : 'Defina o período personalizado'
            }
            description={
              customRangeError
                ? 'Ajuste as datas acima para carregar o dashboard.'
                : 'Informe uma data inicial e uma data final válida para carregar o dashboard.'
            }
          />
        </Card>
      </div>
    )
  }

  if (liveOpen) {
    const cached = queryClient.getQueryData<DashboardOverview>(overviewQuery.queryKey)
    const cachedSeries = cached?.byCurrency[selectedCurrency]?.grossSalesSeries
    return (
      <LiveGrossSales
        filters={{ range, from: search.from, to: search.to, productId: search.productId }}
        placeholder={cachedSeries ? { series: cachedSeries, periodLabel: period.label } : undefined}
        mode={chartMode}
        onModeChange={setChartMode}
        onClose={() => {
          updateSearch({ live: undefined })
        }}
      />
    )
  }

  if (overview.isPending) {
    return <DashboardSkeleton />
  }

  if (overview.isError) {
    return (
      <div className={page} data-testid="dashboard-screen">
        <DashboardHeader />
        <DashboardFilters range={range} search={search} updateSearch={updateSearch} />
        <Card padding="lg">
          <ErrorState
            onRetry={() => {
              void overview.refetch()
            }}
          />
        </Card>
      </div>
    )
  }

  const data = overview.data
  const currencyData = data.byCurrency[selectedCurrency] ?? emptyCurrencyOverview(selectedCurrency)
  const selectedBalances = data.saldo.filter((balance) => balance.currency === selectedCurrency)
  const visibleBalances = selectedBalances.length
    ? selectedBalances
    : [
        {
          currency: selectedCurrency,
          available: { amount: '0.000', currency: selectedCurrency },
          pending: { amount: '0.000', currency: selectedCurrency },
        },
      ]
  const lastUpdatedAt = oldestFinancialComputation([overview, recurrence])

  const refreshDashboard = () => {
    void refreshInTiers()
  }

  const openLive = () => {
    if (typeof document.documentElement.requestFullscreen === 'function') {
      void document.documentElement.requestFullscreen().catch(() => undefined)
    }
    updateSearch({ live: true })
  }

  return (
    <div className={page} data-testid="dashboard-screen">
      <div className={dashboardHeader}>
        <div className={headerCopy}>
          <h1 className={title}>Dashboard de vendas</h1>
          <span className={sectionSubtitle}>
            Vendas criadas, dinheiro recebido e eficiência comercial em reais.
          </span>
        </div>
        <FreshnessBar
          lastUpdatedAt={lastUpdatedAt}
          isFetching={
            overview.isFetching ||
            recurrence.isFetching ||
            awardsPerformance.isFetching ||
            awardsList.isFetching ||
            latestSales.isFetching
          }
          onRefresh={refreshDashboard}
        />
      </div>

      <DashboardFilters
        data={{
          ...data,
          currencies: Array.from(
            new Set([...data.currencies, ...(recurrence.data?.currencies ?? [])]),
          ),
        }}
        range={range}
        search={search}
        selectedCurrency={selectedCurrency}
        updateSearch={updateSearch}
      />

      <div className={dashboardGroup}>
        <GrossSalesChart
          series={currencyData.grossSalesSeries}
          currency={selectedCurrency}
          periodLabel={period.label}
          mode={chartMode}
          onModeChange={setChartMode}
          onExpand={openLive}
        />
        <KpiRow currencyData={currencyData} global={currencyData.indicators} view="all" />
        <FinancialSummaryPanel data={currencyData.financial} />
        <ReferralBalanceCard receivingNotice={referralReceivingNotice} />
        {
          <Card
            padding="md"
            className={balanceTeaser}
            role="region"
            data-testid="dashboard-wallet-balance"
            aria-label={receiptsExperience ? 'Saldo a liberar' : 'Saldo em carteira'}
          >
            <div className={balanceTeaserText}>
              <span className={sectionTitle}>
                {receiptsExperience ? 'Saldo a liberar' : 'Saldo em carteira'}
              </span>
              <span className={sectionSubtitle}>
                {receiptsExperience
                  ? 'Valores aguardando liberação, sem incluir a reserva. Consulte os movimentos em Recebimentos.'
                  : 'Saldo atual em reais. Consulte os movimentos em Recebimentos.'}
              </span>
            </div>
            <div className={balanceTeaserMeta}>
              {visibleBalances.map((saldo) => (
                <span key={saldo.currency}>
                  <MoneyValue
                    amount={(receiptsExperience ? saldo.pending : saldo.available).amount}
                    currency={saldo.available.currency}
                    size="sm"
                  />
                </span>
              ))}
              <Link to="/financas" className={buttonRecipe({ variant: 'text', size: 'sm' })}>
                Ver Recebimentos
              </Link>
            </div>
          </Card>
        }
      </div>

      <>
        <div className={analyticsGrid}>
          <PaymentMethodBreakdown
            methods={currencyData.payMethods}
            pendingPix={currencyData.pendingPix}
          />
          <OriginAnalytics currencyData={currencyData} />
          <TopProducts products={currencyData.topProducts} />
        </div>

        <RecurrencePanel
          data={recurrence.data}
          selectedCurrency={selectedCurrency}
          isPending={recurrence.isPending}
          isError={recurrence.isError}
          onRetry={() => {
            void recurrence.refetch()
          }}
        />

        {latestSales.isError ? (
          <Card padding="md">
            <ErrorState
              onRetry={() => {
                void latestSales.refetch()
              }}
            />
          </Card>
        ) : latestSales.isPending ? (
          <Card padding="md">
            <Skeleton width="100%" height={260} />
          </Card>
        ) : (
          <LatestSales sales={latestSales.data.items} view={view} onViewSale={onViewSale} />
        )}
      </>

      <div ref={awardsRef}>
        <AwardsStrip
          performance={awardsPerformance.data}
          requests={awardsList.data?.items ?? []}
          isPending={awardsPerformance.isPending || awardsList.isPending}
          isError={awardsPerformance.isError || awardsList.isError}
          isRequesting={awardRequest.isPending}
          onRetry={() => {
            void awardsPerformance.refetch()
            void awardsList.refetch()
          }}
          onRequest={async (metaId, endereco) => {
            await awardRequest.mutateAsync({ metaId, endereco })
          }}
        />
      </div>
    </div>
  )
}
